import { PageIndices } from '../../common/i-doc/i-doc.registry.ts';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Stack,
  Alert,
  AlertTitle,
  CircularProgress,
  IconButton,
  Tooltip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
} from '@mui/material';
import TelegramIcon from '@mui/icons-material/Telegram';
import RefreshIcon from '@mui/icons-material/Refresh';
import LinkIcon from '@mui/icons-material/Link';
import BugReportIcon from '@mui/icons-material/BugReport';
import { DashboardLayout } from '@components/DashboardLayout.tsx';
import { PageCard } from '@components/common/PageCard.tsx';
import { PageHint } from '@components/common/PageHint.tsx';
import * as adminTelegramService from '@services/admin-telegram.service.ts';
import { TelegramBotInfo, WebhookStatus, WebhookDiagnoseResponse } from '@services/admin-telegram.service.ts';
import { toastService } from '@services/toast.service.ts';
import { getApiBaseUrl } from '@services/api.ts';

export const AdminTelegramPage: React.FC = () => {
  const [bots, setBots] = useState<TelegramBotInfo[]>([]);
  const [expertUrl, setExpertUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [fetchingBots, setFetchingBots] = useState(true);
  const [webhookStatus, setWebhookStatus] = useState<WebhookStatus | null>(null);
  const [diagnoseInfo, setDiagnoseInfo] = useState<WebhookDiagnoseResponse | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);

  const apiBaseUrl = getApiBaseUrl();
  const suggestedWebhookUrl = `${apiBaseUrl}/telegram/webhook/expert`;

  const fetchBots = async () => {
    try {
      setFetchingBots(true);
      const data = await adminTelegramService.getBotsInfo();
      const botsArray = Array.isArray(data) ? data : [];
      setBots(botsArray);
      
      const expertBot = botsArray.find(b => b.type === 'expert');
      if (expertBot?.webhookInfo?.url) {
        setExpertUrl(expertBot.webhookInfo.url);
        setWebhookStatus(expertBot.webhookInfo);
      } else {
        setExpertUrl(suggestedWebhookUrl);
      }
    } catch (err) {
      console.error('Failed to fetch bots info:', err);
      setBots([]);
      setExpertUrl(suggestedWebhookUrl);
    } finally {
      setFetchingBots(false);
    }
  };

  useEffect(() => {
    fetchBots();
  }, []);

  const handleSetExpertWebhook = async () => {
    if (!expertUrl) {
      toastService.error('Введіть URL вебхука');
      return;
    }

    setLoading(true);
    try {
      await adminTelegramService.setExpertWebhook({ url: expertUrl });
      toastService.success('Вебхук для експертного бота успішно встановлено');
      await fetchBots();
      await handleDiagnose();
    } catch (err) {
      console.error('Failed to set expert webhook:', err);
      toastService.error('Не вдалося встановити вебхук');
    } finally {
      setLoading(false);
    }
  };

  const handleDiagnose = async () => {
    try {
      setDiagnosing(true);
      const result = await adminTelegramService.getExpertWebhookStatus();
      setDiagnoseInfo(result);
      setWebhookStatus(result.webhookInfo);
      toastService.success('Діагностика завершена');
    } catch (err) {
      console.error('Failed to diagnose webhook:', err);
      toastService.error('Не вдалося отримати статус вебхука від Telegram API');
    } finally {
      setDiagnosing(false);
    }
  };

  const expertBot = Array.isArray(bots) ? bots.find(b => b.type === 'expert') : undefined;

  return (
    <DashboardLayout data-i-doc={PageIndices.ADMIN_TELEGRAM}>
      <PageHint iDoc={PageIndices.ADMIN_TELEGRAM} />

      <PageCard>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TelegramIcon color="primary" />
            Telegram Боти та Webhooks
          </Typography>
          <Box>
             <IconButton onClick={handleDiagnose} disabled={diagnosing || fetchingBots} sx={{ mr: 1 }}>
                <Tooltip title="Діагностика вебхука">
                    {diagnosing ? <CircularProgress size={24} /> : <BugReportIcon color="warning" />}
                </Tooltip>
             </IconButton>
             <IconButton onClick={fetchBots} disabled={fetchingBots}>
                <RefreshIcon />
             </IconButton>
          </Box>
        </Box>

        {fetchingBots ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={4}>
            <Paper variant="outlined" sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Expert Bot
                  </Typography>
                  {(expertBot?.username || diagnoseInfo?.botUsername) && (
                    <Typography variant="body2" color="primary">
                      @{expertBot?.username || diagnoseInfo?.botUsername}
                    </Typography>
                  )}
                </Box>
                <Stack direction="row" spacing={1}>
                    <Chip 
                        label={(expertBot?.tokenConfigured || diagnoseInfo?.tokenConfigured) ? "Token OK" : "Token Missing"} 
                        color={(expertBot?.tokenConfigured || diagnoseInfo?.tokenConfigured) ? "success" : "error"}
                        size="small"
                        variant="filled"
                    />
                    {webhookStatus && (
                         <Chip 
                            label={webhookStatus.url && webhookStatus.url.length > 0 ? "Webhook Active" : "Webhook Inactive"} 
                            color={webhookStatus.url && webhookStatus.url.length > 0 ? "success" : "warning"}
                            size="small"
                            variant="filled"
                        />
                    )}
                </Stack>
              </Box>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Цей бот використовується для надсилання сповіщень експертам.
                Для роботи необхідно встановити Webhook URL, на який Telegram буде надсилати повідомлення.
              </Typography>

              <Box sx={{ mb: 3 }}>
                <Typography variant="caption" color="text.secondary" gutterBottom display="block">
                  Поточний вебхук (згідно діагностики):
                </Typography>
                <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'grey.100', p: 1, borderRadius: 1, wordBreak: 'break-all' }}>
                  {webhookStatus?.url || 'НЕ ВСТАНОВЛЕНО'}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 3 }}>
                <AlertTitle>Рекомендований URL для цього сервера</AlertTitle>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {suggestedWebhookUrl}
                  </Typography>
                  <Tooltip title="Використати цей URL">
                    <IconButton size="small" onClick={() => setExpertUrl(suggestedWebhookUrl)}>
                      <LinkIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Alert>

              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="Новий Webhook URL"
                  variant="outlined"
                  value={expertUrl}
                  onChange={(e) => setExpertUrl(e.target.value)}
                  placeholder="https://..."
                  size="small"
                  disabled={loading}
                />
                <Button
                  variant="contained"
                  onClick={handleSetExpertWebhook}
                  disabled={loading || !expertUrl}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Оновити Webhook'}
                </Button>
              </Box>
            </Paper>

            {webhookStatus && (
                <Paper variant="outlined" sx={{ p: 3, borderLeft: '4px solid #ed6c02' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <Typography variant="subtitle1" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <BugReportIcon color="warning" />
                            Результати діагностики Telegram API
                        </Typography>
                        {diagnoseInfo?.env && (
                             <Chip 
                                label={diagnoseInfo.env.NODE_ENV} 
                                size="small" 
                                color={diagnoseInfo.env.NODE_ENV === 'production' ? 'info' : 'default'}
                                variant="outlined"
                            />
                        )}
                    </Box>
                    <Divider sx={{ mb: 1.5 }} />
                    <List dense>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="URL вебхука в Telegram" 
                                secondary={webhookStatus.url || 'Не встановлено'} 
                                secondaryTypographyProps={{ sx: { fontFamily: 'monospace', wordBreak: 'break-all' } }}
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="Очікує оновлень (Pending updates)" 
                                secondary={webhookStatus.pending_update_count} 
                                secondaryTypographyProps={{ color: webhookStatus.pending_update_count > 0 ? 'warning.main' : 'text.secondary' }}
                            />
                        </ListItem>
                        {webhookStatus.last_error_message && (
                            <ListItem disableGutters>
                                <ListItemText 
                                    primary="Остання помилка" 
                                    secondary={`${webhookStatus.last_error_date ? new Date(webhookStatus.last_error_date * 1000).toLocaleString('uk-UA') : 'Нещодавно'}: ${webhookStatus.last_error_message}`}
                                    secondaryTypographyProps={{ color: 'error.main' }}
                                />
                            </ListItem>
                        )}
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="IP адреса сервера Telegram" 
                                secondary={webhookStatus.ip_address || '—'} 
                            />
                        </ListItem>
                        <ListItem disableGutters>
                            <ListItemText 
                                primary="Макс. з'єднань" 
                                secondary={webhookStatus.max_connections} 
                            />
                        </ListItem>
                    </List>
                </Paper>
            )}

            <Alert severity="warning">
              При розробці локально використовуйте <b>ngrok</b> або аналогічні сервіси, щоб прокинути ваш локальний порт у публічний інтернет. 
              Telegram не може надсилати повідомлення на <code>localhost</code> або <code>127.0.0.1</code>.
            </Alert>
          </Stack>
        )}
      </PageCard>
    </DashboardLayout>
  );
};

export default AdminTelegramPage;
